/* LEGEND — chart header + indicator control + cursor legend. Ported from header.jsx. */
import React from 'react';
import { fmtPx4, fmtSignedPx, fmtPct } from '../lib/format';
import { INDICATOR_DEFS, TF_TABS, TF_LABEL, EMA_PERIODS, MA_KIND_COLOR } from '../lib/constants';
import { subscribeQuotes } from '../data/source';

const { useRef: useRefH, useEffect: useEffectH, useState: useStateH } = React;

/** "20:00" — a locate's expiry in exchange time, which is the only clock that decides it. */
function fmtEtClock(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch { return '—'; }
}

export function ChartHeader(props: any) {
  const { symbol, name, lastPrice, prevClose, tf, onTf, indicators, onToggleInd,
    indOpen, setIndOpen, chartType, onChartType, onDetach,
    onJournal, journalOpen, reopens, tradeSide, onTradeSide, sideLocked,
    levelMode, onLevelMode, levelPrefs, onLevelPrefs, heldLocate } = props;
  const chg = prevClose ? (lastPrice - prevClose) / prevClose : 0;
  const chgAbs = lastPrice - prevClose;
  const up = chg >= 0;
  const activeCount = Object.values(indicators || {}).filter(Boolean).length;

  return React.createElement('div', { className: 'chart-head' },
    React.createElement('div', { className: 'sym-block' },
      React.createElement('span', { className: 'sym' }, symbol),
      React.createElement('span', { className: 'sym-name' }, name)),
    React.createElement('div', { style: { display: 'flex', alignItems: 'baseline', gap: 9 } },
      // Fixed-width slots so a changing price/change string can't reflow the row
      // (which would jitter the TF selector beside it). The widths live in CSS, not
      // inline, so the header's collapse ladder can tighten them on a narrow pane —
      // inline styles would win over the container queries and force a wrap.
      React.createElement('span', { className: 'px num' }, fmtPx4(lastPrice)),
      React.createElement('span', { className: 'px-chg num ' + (up ? 'up' : 'down') },
        React.createElement('svg', { width: 9, height: 9, viewBox: '0 0 8 8', style: { transform: up ? 'none' : 'rotate(180deg)', flex: '0 0 auto' } }, React.createElement('path', { d: 'M4 1l3 5H1z', fill: 'currentColor' })),
        fmtSignedPx(chgAbs), ' (', fmtPct(chg), ')')),

    React.createElement(Nbbo, { symbol, lastPrice }),

    React.createElement('div', { className: 'seg', style: { marginLeft: 8 } },
      TF_TABS.map((t) => React.createElement('button', {
        key: t, className: t === tf ? 'on' : '', onClick: () => onTf(t),
      }, TF_LABEL[t] || t))),

    React.createElement('div', { className: 'head-right' },
      // Borrow HELD for this symbol. Present only when there is some, so an
      // empty header means exactly one thing: nothing located here.
      //
      // It sits beside the side toggle because that is where it is acted on —
      // and because the two facts are routinely confused. The Borrow card reads
      // the desk's inventory, which can be zero while these shares, already
      // bought and ours until 20:00 ET, sit ready to short (DCOY 2026-09-22).
      heldLocate && heldLocate.remaining > 0 ? React.createElement('span', {
        className: 'borrow-held' + (heldLocate.used > 0 ? ' part' : ''),
        title: `Borrow held for ${symbol}: ${heldLocate.qty.toLocaleString('en-US')} located`
          + (heldLocate.used > 0 ? `, ${heldLocate.used.toLocaleString('en-US')} already shorted against it` : '')
          + `, ${heldLocate.remaining.toLocaleString('en-US')} left to short`
          + (heldLocate.expiresAt ? ` · expires ${fmtEtClock(heldLocate.expiresAt)} ET` : '')
          + '. Shorting draws this down permanently — covering does not give it back.',
      },
        React.createElement('i', { className: 'bh-dot' }),
        React.createElement('span', { className: 'bh-lbl' }, 'Borrow'),
        React.createElement('b', null, heldLocate.remaining.toLocaleString('en-US')),
        heldLocate.used > 0 ? React.createElement('span', { className: 'bh-of' }, '/' + heldLocate.qty.toLocaleString('en-US')) : null) : null,
      // Trade side. Ported from the prototype (legend/header.jsx:45). Sends
      // set-trading-side to the worker, which owns the armed side; long is the
      // default and the fallback everywhere in that chain.
      onTradeSide ? React.createElement('div', {
        className: 'side-flag' + (sideLocked ? ' locked' : ''), 'data-side': tradeSide || 'long',
        title: sideLocked
          ? 'Side is locked while a position is open — flatten first to flip.'
          : 'Trade side — the direction of the next entry on this symbol.',
      },
        ['long', 'short'].map((sd) => React.createElement('button', {
          key: sd, className: (sd === (tradeSide || 'long') ? 'on ' : '') + sd,
          disabled: !!sideLocked,
          onClick: () => onTradeSide(sd),
        }, React.createElement('span', { className: 'dot' }), sd))) : null,
      // Widget pills (Position / Ladder / Supply / Pullback) — icon + label; the
      // label collapses under the pane container queries so narrow panes keep every
      // affordance as an icon instead of overflowing.
      // Most are pure REOPENS: the caller drops them from the list once their card
      // is up. `r.on` is for the ones that stay mounted as TOGGLES (the docked
      // Ladder), so the pill can carry its open state the way Detach/journal do.
      ...(reopens || []).map((r: any) => React.createElement('button', {
        key: r.key, className: 'pill-btn reopen-' + r.key + (r.on ? ' on' : ''), onClick: r.onClick, title: r.title,
      },
        React.createElement('svg', { viewBox: '0 0 24 24', width: 14, height: 14, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: r.d })),
        React.createElement('span', { className: 'pill-lbl' }, r.label))),
      reopens && reopens.length ? React.createElement('span', { className: 'hr-sep' }) : null,
      onJournal ? React.createElement('button', {
        className: 'pill-btn icon-only' + (journalOpen ? ' on' : ''), onClick: onJournal,
        title: 'Trade journal for ' + symbol + ' — every scale-in / scale-out + realized P&L',
      },
        React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M4 5h16M4 12h16M4 19h10' }))) : null,
      // Detach = the full Terminal for this symbol in its OWN OS window (own
      // WebSocket, every widget) — see lib/solo.ts. Hidden inside a solo
      // window: the symbol is already pinned there.
      onDetach ? React.createElement('button', {
        className: 'pill-btn detach-btn', onClick: onDetach,
        title: 'Detach ' + symbol + ' into its own window — the full terminal, every widget, its own connection. Drag it to any screen.',
      },
        React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M14 4h6v6M20 4l-8 8M18 14v5H5V6h5' })),
        React.createElement('span', { className: 'pill-lbl' }, 'Detach')) : null,
      React.createElement('button', { className: 'pill-btn', onClick: () => setIndOpen(!indOpen), title: 'Indicators' },
        React.createElement('svg', { viewBox: '0 0 24 24', width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
          React.createElement('path', { d: 'M4 6h10M18 6h2M4 12h2M10 12h10M4 18h6M14 18h6' }),
          React.createElement('circle', { cx: 16, cy: 6, r: 2, fill: 'var(--surface-2)' }),
          React.createElement('circle', { cx: 8, cy: 12, r: 2, fill: 'var(--surface-2)' }),
          React.createElement('circle', { cx: 12, cy: 18, r: 2, fill: 'var(--surface-2)' })),
        React.createElement('span', { className: 'pill-lbl' }, 'Indicators'),
        React.createElement('span', { className: 'cnt mono' }, activeCount)),
      React.createElement('div', { className: 'seg' },
        React.createElement('button', { className: chartType === 'candles' ? 'on' : '', onClick: () => onChartType('candles'), title: 'Candles' },
          React.createElement('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M8 6v3M8 15v3M8 9h0M16 4v4M16 16v4' }), React.createElement('rect', { x: 6, y: 9, width: 4, height: 6 }), React.createElement('rect', { x: 14, y: 8, width: 4, height: 8 }))),
        React.createElement('button', { className: chartType === 'line' ? 'on' : '', onClick: () => onChartType('line'), title: 'Line' },
          React.createElement('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }, React.createElement('path', { d: 'M3 16l5-6 4 3 8-9' }))))),

    indOpen ? React.createElement(IndicatorPopover, { tf, indicators, onToggle: onToggleInd, onClose: () => setIndOpen(false), levelMode, onLevelMode, levelPrefs, onLevelPrefs }) : null);
}

/* Level display controls — live INSIDE the Indicators card (below the indicator
   rows) so everything the chart draws is switched from one place. Mode: Main
   (curated, default) · AI · Zones · Near · All as a dropdown; kinds: ticked kinds
   draw in EVERY mode; moving averages default OFF because on runner days they are
   the chop zone, not levels (VEEA 2026-09-15: 32 of 205 map entries). Persisted
   in localStorage as legend.levelPrefs / legend.levelMode (Terminal owns the state). */
const LEVEL_MODES: [string, string, string][] = [
  ['main', 'Main', 'the few that matter: nearest, session high, prior day, proven intraday, next shelves'],
  ['model', 'AI', 'model-ranked P(hold)'],
  ['cluster', 'Zones', 'every level, merged into confluence bands'],
  ['near', 'Near', 'within 6 ATR plus heavyweight structure'],
  ['all', 'All', 'every level, drawn separately'],
];
const LEVEL_CLASS_ROWS = [
  { key: 'structural', name: 'Daily / weekly structure', sub: 'swing highs & lows, clustered into shelves' },
  { key: 'prior', name: 'Prior day / week high · low' },
  { key: 'session', name: 'Session high' },
  { key: 'wall', name: 'Walls', sub: 'absorption candles' },
  { key: 'flipped', name: 'Flipped levels', sub: 'broken resistance now support' },
  { key: 'swing', name: 'Intraday swings', sub: '5m · 15m · 1H · 4H' },
  { key: 'vwap', name: 'VWAP' },
  { key: 'ma', name: 'Moving averages', sub: 'EMA / SMA ribbons' },
];

/* Live NBBO — bid × ask and the spread, in dollars and basis points.
   The spread is `ask - bid` (the ladder engine's own definition, so the header and
   the ladder gauge cannot disagree) shown at the PRICE's precision: a sub-penny
   book on a $0.40 name is the difference between a tradable spread and a wall, and
   rounding it to cents would erase that.

   Basis points because that is the unit the cost actually lives in — $0.01 is
   nothing on a $50 stock and 250bp of instant drawdown on a $0.40 one.

   Staleness is explicit. A halted or thinly-quoted symbol simply stops producing
   quotes, and a book frozen 40 seconds ago rendered identically to a live one is
   how a stale number gets traded on; past STALE_MS the readout dims and says so. */
const STALE_MS = 12_000;

function Nbbo({ symbol, lastPrice }: any) {
  // The strip OWNS the quote feed (HeatStrip rule): quotes:{SYM} is the
  // chattiest channel in the app — holding the book in Terminal state made
  // every NBBO tick re-render the entire tree at up to display-refresh rate
  // on busy tape. Here a book tick re-renders these ~20 elements, nothing else.
  // Quotes land in a ref and a single rAF publishes the newest one — at most
  // one strip render per frame no matter the tape.
  const [nbbo, setNbbo] = useStateH<any>(null);
  const quoteRef = useRefH<any>(null);
  const quoteRafRef = useRefH<number | null>(null);
  useEffectH(() => () => { if (quoteRafRef.current != null) cancelAnimationFrame(quoteRafRef.current); }, []);
  useEffectH(() => {
    setNbbo(null); // never show the previous symbol's book against this one's price
    quoteRef.current = null;
    if (!symbol) return;
    const off = subscribeQuotes(symbol, (q: any) => {
      const bid = Number(q.bid), ask = Number(q.ask);
      // The screener already drops inverted and zero/missing books before publishing;
      // re-check here so a future producer change can't put a negative spread on screen.
      if (!Number.isFinite(bid) || !Number.isFinite(ask) || bid <= 0 || ask <= 0 || ask < bid) return;
      const ts = q.timestamp ? new Date(q.timestamp).getTime() : Date.now();
      quoteRef.current = { bid, ask, ts: Number.isFinite(ts) ? ts : Date.now() };
      if (quoteRafRef.current == null) {
        quoteRafRef.current = requestAnimationFrame(() => {
          quoteRafRef.current = null;
          setNbbo(quoteRef.current);
        });
      }
    });
    return () => { off(); };
  }, [symbol]);
  const [, forceTick] = useStateH(0);
  // Staleness is a function of elapsed time, not of new data — without a heartbeat a
  // book that STOPS updating would keep rendering fresh forever, which is the exact
  // failure this is meant to surface. One cheap tick a second only while mounted.
  useEffectH(() => {
    const iv = setInterval(() => forceTick((n: number) => n + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!nbbo || !nbbo.bid || !nbbo.ask) {
    return React.createElement('div', { className: 'nbbo empty', title: 'No quote for this symbol yet' },
      React.createElement('span', { className: 'nb-k' }, 'BID × ASK'),
      React.createElement('span', { className: 'nb-v num' }, '—'));
  }
  const { bid, ask, ts } = nbbo;
  const spread = ask - bid;
  const prec = (lastPrice || ask) < 1 ? 4 : 2;
  const mid = (bid + ask) / 2;
  const bp = mid > 0 ? (spread / mid) * 10_000 : 0;
  const stale = Date.now() - ts > STALE_MS;
  const age = Math.round((Date.now() - ts) / 1000);
  // Wide spreads are a cost, not a detail — flag the band where taking the offer
  // starts to dominate the trade (see the BUY_NOW slippage audit).
  const wide = bp >= 100;
  return React.createElement('div', {
    className: 'nbbo' + (stale ? ' stale' : '') + (wide ? ' wide' : ''),
    title: stale
      ? 'Last NBBO was ' + age + 's ago — the book has stopped updating (halt, or nothing quoting)'
      : 'National best bid × best offer, live',
  },
    React.createElement('span', { className: 'nb-v num' },
      bid.toFixed(prec), React.createElement('span', { className: 'nb-x' }, '×'), ask.toFixed(prec)),
    React.createElement('span', { className: 'nb-sp num' },
      '$' + spread.toFixed(prec),
      React.createElement('span', { className: 'nb-bp' }, Math.round(bp) + 'bp')),
    stale ? React.createElement('span', { className: 'nb-stale' }, age + 's') : null);
}

export function IndicatorPopover({ tf, indicators, onToggle, onClose, levelMode, onLevelMode, levelPrefs, onLevelPrefs }: any) {
  const ref = useRefH<any>(null);
  useEffectH(() => {
    const h = (e: any) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.pill-btn')) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return React.createElement('div', { className: 'ind-pop', ref },
    React.createElement('div', { className: 'ip-head' },
      React.createElement('div', { className: 'h1' }, 'Indicators'),
      React.createElement('div', { className: 'h2' }, 'Applies to the ', React.createElement('span', { className: 'tf-tag' }, tf + 'm'), ' chart only')),
    INDICATOR_DEFS.map((d) => {
      const on = !!(indicators && indicators[d.key]);
      return React.createElement('div', { key: d.key, className: 'ind-row', onClick: () => onToggle(d.key) },
        React.createElement('span', { className: 'ir-sw', style: { background: d.color } }),
        React.createElement('span', { className: 'ir-name' }, d.name, d.sub ? React.createElement('span', { className: 'ir-sub' }, ' ' + d.sub) : null,
          d.pane === 'sub' ? React.createElement('span', { className: 'ir-sub' }, ' · sub-pane') : null),
        React.createElement('span', { className: 'toggle' + (on ? ' on' : '') }));
    }),
    onLevelPrefs ? React.createElement(LevelControls, { levelMode, onLevelMode, prefs: levelPrefs, onPrefs: onLevelPrefs }) : null);
}

// Structure-map controls, rendered at the bottom of the Indicators card. Every
// change applies on the click: the state lives in Terminal and feeds
// structureView through the srView memo — no reload involved.
function LevelControls({ levelMode, onLevelMode, prefs, onPrefs }: any) {
  const p = prefs || {};
  const set = (k: string, v: any) => onPrefs({ ...p, [k]: v });
  const perSide = Number(p.perSide) || 5;
  return React.createElement('div', { className: 'ip-levels' },
    React.createElement('div', { className: 'ip-section' },
      React.createElement('div', { className: 'ips-h' }, 'Levels', React.createElement('span', { className: 'ips-sub' }, ' structure map · applies on click')),
      React.createElement('label', { className: 'lv-select-row' },
        React.createElement('span', { className: 'ir-name' }, 'Show'),
        React.createElement('select', {
          className: 'lv-select', value: levelMode || 'main',
          onChange: (e: any) => onLevelMode && onLevelMode(e.target.value),
        }, LEVEL_MODES.map(([k, lbl]) => React.createElement('option', { key: k, value: k }, lbl)))),
      React.createElement('div', { className: 'lv-hint' }, (LEVEL_MODES.find(([k]) => k === (levelMode || 'main')) || LEVEL_MODES[0])[2])),
    LEVEL_CLASS_ROWS.map((d) => {
      const on = !!p[d.key];
      return React.createElement('div', { key: d.key, className: 'ind-row', onClick: () => set(d.key, !on) },
        React.createElement('span', { className: 'ir-name' }, d.name, d.sub ? React.createElement('span', { className: 'ir-sub' }, ' ' + d.sub) : null),
        React.createElement('span', { className: 'toggle' + (on ? ' on' : '') }));
    }),
    React.createElement('div', { className: 'ind-row', onClick: () => set('shortTag', !p.shortTag) },
      React.createElement('span', { className: 'ir-name' }, 'Short-candidate tag', React.createElement('span', { className: 'ir-sub' }, ' Main · 75%+ run from the session base (50% = watch)')),
      React.createElement('span', { className: 'toggle' + (p.shortTag ? ' on' : '') })),
    React.createElement('div', { className: 'ind-row lv-count' },
      React.createElement('span', { className: 'ir-name' }, 'Levels per side', React.createElement('span', { className: 'ir-sub' }, ' Main')),
      React.createElement('span', { className: 'seg', style: { marginLeft: 'auto' } },
        React.createElement('button', { onClick: (e: any) => { e.stopPropagation(); set('perSide', Math.max(2, perSide - 1)); } }, '−'),
        React.createElement('span', { className: 'mono lv-n' }, String(perSide)),
        React.createElement('button', { onClick: (e: any) => { e.stopPropagation(); set('perSide', Math.min(10, perSide + 1)); } }, '+'))),
    // What the rail's numbers mean (MV-5 lane: two reads side by side).
    React.createElement('div', { className: 'lv-hint lv-key' },
      'Rail numbers: ', React.createElement('b', { className: 'k-up' }, 'green'), '/', React.createElement('b', { className: 'k-down' }, 'red'),
      ' = chance the level holds (grey = unsure, dashed box = expect it to give way) · ',
      React.createElement('b', { className: 'k-reach' }, 'blue'),
      ' (MV-5) = chance price gets there within 1h, on the side you trade toward: above when Long, below when Short'));
}

export function CursorLegend({ symbol, indicators, hover }: any) {
  const rows: any[] = [];
  const vals = (hover && hover.vals) || {};
  INDICATOR_DEFS.forEach((d) => {
    if (d.key === 'emas' || d.key === 'mas') {
      if (!indicators[d.key]) return;
      const kind = d.key === 'emas' ? 'ema' : 'ma';
      const color = MA_KIND_COLOR[kind];
      for (const key of Object.keys(EMA_PERIODS)) {
        const vk = kind === 'ema' ? key : 'sma' + EMA_PERIODS[key];
        const v = vals[vk];
        rows.push(React.createElement('div', { className: 'lg-row', key: vk },
          React.createElement('span', { className: 'sw', style: { background: color } }),
          React.createElement('span', { style: { color, fontWeight: 600 } }, (kind === 'ema' ? 'EMA ' : 'MA ') + EMA_PERIODS[key]),
          React.createElement('span', { className: 'lv' }, v != null ? fmtPx4(v) : '—')));
      }
      return;
    }
    if (!indicators[d.key] || d.pane === 'sub') return;
    const v = vals[d.key];
    rows.push(React.createElement('div', { className: 'lg-row', key: d.key },
      React.createElement('span', { className: 'sw', style: { background: d.color } }),
      React.createElement('span', { style: { color: d.color, fontWeight: 600 } }, d.name + (d.sub ? ' ' + d.sub : '')),
      React.createElement('span', { className: 'lv' }, v != null ? fmtPx4(v) : '—')));
  });
  const o = hover && hover.ohlc;
  return React.createElement('div', { className: 'legend' },
    React.createElement('div', { className: 'lg-row', style: { marginBottom: 2 } },
      React.createElement('span', { style: { color: 'var(--t2)', fontWeight: 700, letterSpacing: '.04em' } }, symbol),
      o ? React.createElement('span', { className: 'lv', style: { color: o.close >= o.open ? 'var(--up)' : 'var(--down)' } },
        'O ' + fmtPx4(o.open) + '  H ' + fmtPx4(o.high) + '  L ' + fmtPx4(o.low) + '  C ' + fmtPx4(o.close)) : null),
    rows);
}
